// Plan-BundlesImplementation §E.1 — One-click confirm modal used by
// Voice / LiveAvatar bundles (no allocation form needed — single
// resource, fixed quantity per SKU). ($0.10 Test Bundle retired
// 2026-05-11 per CEO directive — test workspaces use Stripe test-mode
// 4242 cards to buy the real SKUs.)
//
// Plan-TextVoicePoolBilling-2026-08 §D added `text_topup` here rather
// than as a fourth modal component: the pooled Text Top-Up is a flat
// single-SKU purchase with NO allocation, which is precisely the shape
// this modal already is. The five-category TextBundleModal is untouched
// and stays the rollback path for flag-off.
//
// On Purchase: hits the bundle-specific endpoint via billingAPI, then
// `window.location.href = checkout_url` to redirect to Stripe Checkout.
//
// 🚨 STRIPE-ONLY. Reached only from BundleCards / the pooled usage bar,
// both of which render inside StripeBillingPage. ShopifyBillingView must
// never mount this — Shopify purchases go through Managed Pricing.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X, CreditCard } from 'lucide-react';
import { billingAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { TEXT_TOPUP_TOKENS, TEXT_TOPUP_TOTAL_USD } from '../../types/billing';
import { formatTokenCount } from './usageBars';

export type SimpleBundleKind = 'voice' | 'liveavatar' | 'text_topup';

interface Props {
  kind: SimpleBundleKind;
  workspaceId: string;
  onClose: () => void;
}

interface CopyEntry {
  title: string;
  body: React.ReactNode;
  buttonLabel: string;
  fineprint?: string;
}

const COPY: Record<SimpleBundleKind, CopyEntry> = {
  text_topup: {
    title: `Text Top-Up — $${TEXT_TOPUP_TOTAL_USD}.00`,
    body: (
      <>
        <p className="mb-3">
          Adds{' '}
          <span className="font-semibold">
            {formatTokenCount(TEXT_TOPUP_TOKENS)} AI text tokens
          </span>{' '}
          to this workspace's shared pool.
        </p>
        <p className="mb-3">
          Nothing to allocate — every AI text feature (chat, email replies,
          knowledge, scrapes, reports, Orchestrator) draws from the one pool,
          so these tokens go wherever you use them.
        </p>
        {/* 🚨 THIS LINE HAS NOW BEEN WRONG IN BOTH DIRECTIONS — check against
            the owner decision, not against a previous version of itself.
              1. Originally "expire 12 months from purchase" (copied from the
                 Voice / LiveAvatar credits) — false, they were cycle-scoped.
              2. Then "apply to your current billing cycle and do not carry
                 over" — correct at the time, false as of the owner's
                 2026-08-13 decision that top-ups NEVER EXPIRE.
            A purchased balance is now durable: it stays on the workspace until
            it is spent. Voice / LiveAvatar are still genuine 365-day credits —
            see the entries below — so the two lifetimes must stay distinct.

            🚨 Deliberately says nothing about WHICH allowance is spent first.
            The consumption order is the backend's business and I have not had
            it confirmed; asserting it here would be the third wrong version of
            this sentence. "Sits on top of" is true under either ordering. */}
        {/* Consumption order is now CONFIRMED at the source (Storm, off
            `79d3c11`): plan allowance first, top-up balance only once it runs
            out. Stated explicitly because without it the most common support
            question is "I bought tokens and my balance didn't move" — which is
            correct behaviour for anyone still inside their plan limit, and
            reads like a failed purchase. */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          These tokens <span className="font-medium">never expire</span> — they
          stay on your workspace until you use them, across every AI text
          feature. Each cycle your plan allowance is used first; your purchased
          balance is only drawn on once that runs out.
        </p>
      </>
    ),
    buttonLabel: `Purchase — $${TEXT_TOPUP_TOTAL_USD}.00`,
    fineprint: 'Workspace-bound. No refunds — see our billing policy.',
  },
  voice: {
    title: 'Voice Bundle — $7.00',
    body: (
      <>
        <p className="mb-3">
          Adds <span className="font-semibold">80 voice minutes</span> to this workspace
          at <span className="font-semibold">$0.0875 / min</span>.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Credits expire 12 months from purchase. Consumed oldest-first when your
          monthly base allowance is exhausted.
        </p>
      </>
    ),
    buttonLabel: 'Purchase — $7.00',
    fineprint: 'Workspace-bound. No refunds — see our billing policy.',
  },
  liveavatar: {
    title: 'LiveAvatar Bundle — $25.00',
    body: (
      <>
        <p className="mb-3">
          Adds <span className="font-semibold">50 LiveAvatar minutes</span> to this
          workspace at <span className="font-semibold">$0.50 / min</span>.
        </p>
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
          ⚠ LiveAvatar is fail-closed when exhausted — no auto-overage. Sessions
          will not start once your minutes run out. Top up again to resume.
        </p>
        {/* 🚨 Owner ruling 2026-08-20 — the bundle is an ACCESSORY of the
            add-on: remaining minutes are FORFEITED when the add-on ends
            (`revoke_liveavatar_credits_on_addon_loss`). "Expire 12 months from
            purchase" alone implied a 12-month life independent of the plan,
            which is now false and is the kind of copy a refund argument is
            built on. Voice is untouched — it has no add-on to lapse. */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Credits expire 12 months from purchase, and can only be used while your
          Live Avatar plan is active — if the plan ends, unused minutes are lost.
        </p>
      </>
    ),
    buttonLabel: 'Purchase — $25.00',
    fineprint: 'Workspace-bound. No refunds.',
  },
};

const SimpleBundleModal: React.FC<Props> = ({ kind, workspaceId, onClose }) => {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const copy = COPY[kind];

  const handlePurchase = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const apiCall = {
        voice: () => billingAPI.buyVoiceBundle({ workspace_id: workspaceId }),
        liveavatar: () => billingAPI.buyLiveAvatarBundle({ workspace_id: workspaceId }),
        text_topup: () => billingAPI.buyTextTopUp({ workspace_id: workspaceId }),
      }[kind];
      const res = await apiCall();
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      showToast('Checkout session created — redirecting…', 'success');
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.detail;
      // 🚨 THIS ENDPOINT RETURNS TWO DIFFERENT `detail` SHAPES. The 409
      // (`workspace_uses_shopify_billing`) sends a STRING; the 403
      // (`plan_required_for_topup`) sends an OBJECT carrying `error`,
      // `message`, `plan` and `subscription_status`. Reading only the string
      // form drops the object case into the generic "could not create
      // session" fallback — which is exactly the refusal the customer most
      // needs the real wording for, since it tells them what to do next.
      //
      // Both nestings are accepted (`detail.message` and a top-level
      // `message`) so a FastAPI wrapper change cannot silently mute this.
      const structured =
        detail && typeof detail === 'object'
          ? (detail as { message?: string })
          : data && typeof data?.message === 'string'
            ? (data as { message?: string })
            : null;
      if (err?.response?.status === 404) {
        showToast('Bundle purchases are not yet available. Coming soon.', 'error');
      } else if (typeof detail === 'string') {
        showToast(detail, 'error');
      } else if (structured?.message) {
        showToast(structured.message, 'error');
      } else {
        showToast('Could not create Stripe Checkout session', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{copy.title}</h3>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 text-sm text-gray-700 dark:text-gray-300">
          {copy.body}
          {copy.fineprint && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3">
              {copy.fineprint}
            </p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-purple-600 hover:shadow-lg"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            {copy.buttonLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SimpleBundleModal;
